import { Component } from '@theme/component';
import { fetchShortestActiveSweepstake } from '@theme/clean-sweeps';

/**
 * Announcement bar: overflow marquee for messages, plus a live countdown.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [messages]
 * @property {HTMLElement} [marquee]
 * @property {HTMLElement} [track]
 *
 * @extends {Component<Refs>}
 */
export class AnnouncementBar extends Component {
  /** @type {ResizeObserver | undefined} */
  #resizeObserver;

  connectedCallback() {
    super.connectedCallback();
    this.#syncHeight();
    this.#setupMarquee();
    this.#resizeObserver = new ResizeObserver(() => {
      this.#syncHeight();
      this.#setupMarquee();
    });
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
  }

  #syncHeight() {
    document.body.style.setProperty('--announcement-bar-height', `${this.offsetHeight}px`);
  }

  #setupMarquee() {
    const { messages, marquee, track } = this.refs;
    if (!messages || !marquee || !track) return;
    if (window.Shopify?.designMode) return;

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const clone = marquee.querySelector('[data-announcement-clone]');
    const needsMarquee = !reduceMotion && track.scrollWidth > messages.clientWidth + 1;

    this.classList.toggle('is-marquee', needsMarquee);

    if (needsMarquee && !clone) {
      const copy = /** @type {HTMLElement} */ (track.cloneNode(true));
      copy.setAttribute('aria-hidden', 'true');
      copy.setAttribute('data-announcement-clone', '');
      copy.removeAttribute('ref');
      marquee.append(copy);
    } else if (!needsMarquee && clone) {
      clone.remove();
    }
  }
}

/**
 * Countdown to a section-configured end date.
 * Weekly mode rolls forward by 7 days after Sunday 11:59 PM so an open tab
 * keeps counting toward the next draw without a refresh.
 *
 * @typedef {object} CountdownRefs
 * @property {HTMLElement} days
 * @property {HTMLElement} hours
 * @property {HTMLElement} minutes
 * @property {HTMLElement} seconds
 *
 * @extends {Component<CountdownRefs>}
 */
class AnnouncementCountdown extends Component {
  requiredRefs = ['days', 'hours', 'minutes', 'seconds'];

  /** @type {number | undefined} */
  #interval;

  connectedCallback() {
    super.connectedCallback();
    if (this.dataset.mode === 'sweepstake') this.#loadSweepstakeEnd();
    this.#tick();
    this.#interval = window.setInterval(() => this.#tick(), 1000);
  }

  /** Replaces the computed Sunday date with the real sweepstake end once the API answers. */
  async #loadSweepstakeEnd() {
    const sweepstake = await fetchShortestActiveSweepstake(this.dataset.shopGid);
    const endMs = Number(sweepstake?.endDate);
    if (!endMs) return;

    this.dataset.endTs = String(Math.floor(endMs / 1000));
    this.#tick();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.#interval) clearInterval(this.#interval);
  }

  /** @returns {number} */
  #endMs() {
    let end = Number(this.dataset.endTs) * 1000;
    if (Number.isNaN(end)) return NaN;

    if (this.dataset.mode === 'weekly') {
      const weekMs = 7 * 24 * 60 * 60 * 1000;
      while (end <= Date.now()) {
        end += weekMs;
      }
    }

    return end;
  }

  #tick() {
    const end = this.#endMs();
    const remaining = Number.isNaN(end) ? 0 : Math.max(0, end - Date.now());
    const totalSeconds = Math.floor(remaining / 1000);

    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    this.refs.days.textContent = pad(days);
    this.refs.hours.textContent = pad(hours);
    this.refs.minutes.textContent = pad(minutes);
    this.refs.seconds.textContent = pad(seconds);
  }
}

/**
 * @param {number} value
 * @returns {string}
 */
function pad(value) {
  return String(value).padStart(2, '0');
}

if (!customElements.get('announcement-bar-component')) {
  customElements.define('announcement-bar-component', AnnouncementBar);
}

if (!customElements.get('announcement-countdown-component')) {
  customElements.define('announcement-countdown-component', AnnouncementCountdown);
}
